import { base64Uint8Array } from "../lib/utils/base64Uint8Array";
import { type ContentSource, ContentSourceBodyFormDataKey, ContentSourceBodyFormDataValue, ContentSourceCookieNameSent, ContentSourceCookieValueSent, ContentSourceHeaderNameReceived, ContentSourceHeaderNameSent, ContentSourceHeaderValueReceived, ContentSourceHeaderValueSent, ContentSourceUrlComponent, bodyStringToContentSources, urlToContentSourceBases } from '../lib/ContentSource';
import { DbRecordedEvent, RecordedEvent } from "../State/EvidenceDB/DatabaseTypes";
import type { NetworkEvent, NetworkEventDebuggerNetworkRequestWillBeSent, NetworkEventOnBeforeRequest } from '../lib/Events/NetworkEvent';
import { StandardHttpHeaderNamesLowerCase, HttpHeadersToIgnoreValuesOfLc } from '../lib/StandardHttpHeaderNames';
import type { DebuggerEventOrMetaEvent, DevToolsProtocol } from '@uppajung/hardy-har';
import { isNetworkEvent } from '../lib/Events';
import { contentOrBase64DecodedContent } from '../lib/contentOrBase64DecodedContent';

const base64RegExp = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

const decodeRawBytes = (bytes: string | ArrayBuffer): string => {
	if (typeof bytes === "string") {
		if (base64RegExp.test(bytes)) {
			try {
				return new TextDecoder("utf-8", {fatal: true}).decode(base64Uint8Array(bytes));
			} catch {
				return bytes;
			}
		}
		return bytes;
	} else {
		try {
			return new TextDecoder().decode(bytes as ArrayBuffer)
		} catch {
			try {
				return btoa(String.fromCharCode(...new Uint8Array(bytes)));
			} catch {
				return "";
			}
		}
	}
}

const requestHeadersNameValuePairsToContentSources = (requestHeaders: {name: string; value?: string;}[]): ContentSource[] => {
	if (requestHeaders == null) return [];
	const cookies = requestHeaders.find(h => h.name.toLowerCase() === 'cookie')?.value?.split(";")?.map( c => {
		const [name="", value=""] = c.trim().split("=");
		return {name, value};
	}) ?? [];
//					const referer = requestHeaders.find(h => h.name.toLowerCase() === 'referer')?.value;
	const otherHeaders = requestHeaders.filter(
		h => h.name.toLowerCase() !== 'cookie' &&
		h.name.toLowerCase() !== 'referer' && 
		!HttpHeadersToIgnoreValuesOfLc.has(h.name.toLocaleLowerCase())
	);
	const contentSourcesForHeaderValues = otherHeaders.map(h => ({
		direction: 'sent', location: 'header:value', headerName: h.name, ...contentOrBase64DecodedContent(h.value)
	} satisfies ContentSourceHeaderValueSent))
	.filter( c => c.content.length > 0);
	// The only content of the header names that is worth searching are the non-standard names.
	const contentSourcesForHeaderNames = otherHeaders
		.filter( h => !StandardHttpHeaderNamesLowerCase.has(h.name.toLocaleLowerCase()) )
		.map(h => ({ direction: 'sent', location: 'header:name', ...contentOrBase64DecodedContent(h.name)
								} satisfies ContentSourceHeaderNameSent)
		);
	const contentSourcesCookieNames = cookies.filter( c => c.name.length > 0 ).map(c => ({
		 direction: 'sent', location: 'cookie:name', ...contentOrBase64DecodedContent(c.name)
	} satisfies ContentSourceCookieNameSent));
	const contentSourcesForCookieValues = cookies.filter( c => c.value.length > 0 ).map(c => ({
		direction: 'sent', location: 'cookie:value', cookieName: c.name, ...contentOrBase64DecodedContent(c.value)
	} satisfies ContentSourceCookieValueSent))
	// const otherHeadersJson = JSON.stringify(requestHeaders.filter(h => h.name.toLowerCase() !== 'cookie' && h.name.toLowerCase() !== 'referer'));
	return [
		...contentSourcesForHeaderValues, ...contentSourcesForHeaderNames,
		...contentSourcesCookieNames, ...contentSourcesForCookieValues,
	];
}

const debuggerHeadersToNameValuePairs = (headers: DevToolsProtocol.Network.Headers): {name: string; value?: string;}[] => {
	return Object.entries(headers).reduce( (headerList, [name, value]) => {
		headerList.push({name, value});
		return headerList;
	}, [] as {name: string; value: string;}[]);
}

const debuggerRequestHeadersToContentSources = (headers: DevToolsProtocol.Network.Headers) => 
	requestHeadersNameValuePairsToContentSources(debuggerHeadersToNameValuePairs(headers));


export const eventsToContentSources = (event: DbRecordedEvent[]): [DbRecordedEvent<NetworkEvent>, ContentSource][] => {
	const result = [] as [DbRecordedEvent<NetworkEvent>, ContentSource][] ;
	for (const e of event) {
		if (isNetworkEvent(e)) {
			for (const contentSource of eventToContentSources(e)) {
				result.push([e, contentSource]);
			}
		}
	}
	return result;
};

export type JSON = string | number | boolean | {[key: string]: JSON} | JSON[] | null;

export const onBeforeRequestRawToContentSources = (event: RecordedEvent<NetworkEventOnBeforeRequest>, uploadData: chrome.webRequest.UploadData[]): ContentSource[] => {
	return ([] as ContentSource[]).concat(
		...uploadData.map( (uploadDataEntry, bodyIndex) => {
			// May have been converted to a string when written to file
			const contentStringOrArrayBuffer = uploadDataEntry.bytes as ArrayBuffer | string | undefined;
			if (contentStringOrArrayBuffer == null) return [];
			const content = decodeRawBytes(contentStringOrArrayBuffer);
			return bodyStringToContentSources(content).map( c => ({...c, networkEvent: event}));
		})
	);
}

export interface EventToContentSourcesOptions {
	urlHandling?: 'single-string' | 'components';
}
export const eventToContentSources = (event: RecordedEvent, preferDebuggerEvents: boolean = true): ContentSource[] => {
	switch (event.category) {
		case 'Debugger.Network':
			switch (event.eventName) {
				case "Network.webSocketCreated":
					// Count connections only once at the time the connection is created
					return urlToContentSourceBases(event.raw.url);
				case "Network.webSocketFrameSent":
					return [{ direction: 'sent', location: 'frame', ...contentOrBase64DecodedContent(event.raw.response.payloadData), isBase64Encoded: event.raw.response.opcode != 1 }];
				case "Network.webSocketFrameReceived":
					return [{ direction: 'received', location: 'frame', ...contentOrBase64DecodedContent(event.raw.response.payloadData), isBase64Encoded: event.raw.response.opcode != 1 }];
				case "Network.getResponseBodyResponse": {
						const { body: content, base64Encoded: isBase64Encoded=false } = event.raw;
						if (content == null) return [];
						return [{ direction: 'received', location: 'body', ...contentOrBase64DecodedContent(content), isBase64Encoded}];
					}
					case "Network.loadingFinished": {
					if (!("body" in event)) return [];
					const { body: content, base64Encoded: isBase64Encoded=false } = event.raw as Partial<DebuggerEventOrMetaEvent<"Network.getResponseBodyResponse">>;
					if (content == null) return [];
					return [{ direction: 'received', location: 'body', ...contentOrBase64DecodedContent(content), isBase64Encoded}];
				}
				case "Network.requestWillBeSent": {
					const {request} = event.raw;
					let contentSources = [] as ContentSource[];
					event.raw.documentURL;
					const {url, urlFragment, postDataEntries, headers} = request;
					contentSources.push(...urlToContentSourceBases(url));
					// if (urlFragment != null && urlFragment.length > 0) {
					// 	contentSources.push({
					// 		positionWithinUrl: url.length,
					// 		direction: 'sent', location: 'url:hash', ...decodeContent(urlFragment
					// 	} satisfies ContentSourceUrlComponent);
					// }
					if (postDataEntries != null) {
						for (const {bytes} of postDataEntries) {
							if (bytes != null && bytes.length > 0) {
								contentSources.push(...bodyStringToContentSources(bytes));
							}
						}
					}
					contentSources.push(...debuggerRequestHeadersToContentSources(headers));
					return contentSources;
				}
				case "Network.requestWillBeSentExtraInfo": {
					let contentSources = [] as ContentSource[];
					const {headers, associatedCookies} = event.raw;
					contentSources.push(...debuggerRequestHeadersToContentSources(headers));
					if (associatedCookies != null) {
						for (const associatedCookie of associatedCookies) {
							if (associatedCookie.blockedReasons?.length > 0) continue;
							const {name, value} = associatedCookie.cookie;
							contentSources.push({direction: 'sent', location: 'cookie:name', ...contentOrBase64DecodedContent(name)} satisfies ContentSourceCookieNameSent);
							contentSources.push({direction: 'sent', location: 'cookie:value', cookieName: name, ...contentOrBase64DecodedContent(value)} satisfies ContentSourceCookieValueSent);
						}
					}
					return contentSources;
				}
				case "Network.dataReceived": {
					return [];
				}
				case "Network.resourceChangedPriority": {
					return [];
				}
				case "Network.responseReceivedExtraInfo": {
					return [];
				}
				case "Network.responseReceived": {
					if (!preferDebuggerEvents) return [];
					const {requestHeaders} = event.raw.response;
					const { headers } = event.raw.response;
					if (headers == null) return [];
					const contentSourcesForHeaderValues = Object.entries(headers)
						.filter( ([headerName, headerValue ]) => (
							!HttpHeadersToIgnoreValuesOfLc.has(headerName.toLocaleLowerCase()) &&
								headerValue.length > 0)
						).map( ([headerName, content]) => ({
							direction: 'received', location: 'header:value', headerName, content
						} satisfies ContentSourceHeaderValueReceived ))
						.filter( c => c.content.length > 0 && !HttpHeadersToIgnoreValuesOfLc.has(c.headerName.toLocaleLowerCase()) );
					// The only content of the header names that is worth searching are the non-standard names.
					const headerNames = Object.keys(headers);
					const contentSourcesForHeaderNames = headerNames
						.filter( headerName => !StandardHttpHeaderNamesLowerCase.has(headerName.toLocaleLowerCase()) && !HttpHeadersToIgnoreValuesOfLc.has(headerName.toLocaleLowerCase()) )
						.map(content => ({direction: 'received', location: 'header:name', content} satisfies ContentSourceHeaderNameReceived));
					return [
						...(requestHeaders == null ? [] : debuggerRequestHeadersToContentSources(requestHeaders)),
						...contentSourcesForHeaderValues, ...contentSourcesForHeaderNames,
					];
				}
			}
			break;
		case "WebRequest": {
//			if (preferDebuggerEvents) return [];
			// ProcessInfo.log("WebRequest event", event);
			switch (event.eventName) {
				case "WebRequest.beforeRequest": {
					const { requestBody } = event.raw;
					const { formData, raw=[] } = requestBody ?? {};

					return [
						...urlToContentSourceBases(event.raw.url),
					...((formData == null) ? [] :
					([] as ContentSource[]).concat(
							...Object.entries(formData).map( ([key, values]) => ([
								{  direction: 'sent', location: 'body:form:key', ...contentOrBase64DecodedContent(key) } satisfies ContentSourceBodyFormDataKey,
								...values.map( (value) => ({  direction: 'sent', location: 'body:form:value', key, ...contentOrBase64DecodedContent(value) }satisfies ContentSourceBodyFormDataValue) )
							] as ContentSource[]
						)))
					),
					...onBeforeRequestRawToContentSources(event, raw)
				];
				}
				case "WebRequest.sendHeaders": {
					const { requestHeaders } = event.raw;
					if (requestHeaders == null) return [];
					return requestHeadersNameValuePairsToContentSources(requestHeaders);
				}
				break;
			}
		}
	}
	return [];
};
